import AppConfig from "@/AppConfig";
import { RHI_QUERY_KEYS, RHI_TIMEOUT_VALUES, RHI_TTL_VALUES } from "@/constants/queryKeys";
import {
  IGetQuotesRequest,
  IPurchaseIntentPayload,
} from '@/types';
import {
  IRhiV2AffirmRequest,
  IRhiV2AffirmResponse,
  IRhiV2CommitRequest,
  IRhiV2CreateQuoteResponse,
  IRhiV2JourneyList,
  IRhiV2KycAcceptPriceRequest,
  IRhiV2KycAcceptPriceResponse,
  IRhiV2KycForm,
  IRhiV2KycInitiateRequest,
  IRhiV2KycInitiateResponse,
  IRhiV2KycStateResponse,
  IRhiV2KycVerifyOtpRequest,
  IRhiV2PaymentInitiateRequest,
  IRhiV2PaymentInitiateResponse,
  IRhiV2PaymentStatus,
  IRhiV2PaymentSummary,
  IRhiV2IfscLookup,
  IRhiV2IfscResponse,
  IRhiV2PincodeLookup,
  IRhiV2PincodeResponse,
  IRhiV2PolicyDocument,
  IRhiV2ProposalForm,
  IRhiV2QuoteRequest,
  IRhiV2QuoteResponse,
  IRhiV2SaveAnswersRequest,
  IRhiV2SaveAnswersResponse,
  IRhiV2UploadDocumentRequest,
  IRhiV2UploadDocumentResponse,
  IRhiV2Selection,
  IRhiV2SelectionRequest,
  IRhiV2SendForReviewResponse,
  IRhiV2SubmitRequest,
  IRhiV2SubmitResponse,
} from '@/types/rhiV2';
import {
  decodeAuthToken,
  employeeExperienceAxiosInstance,
  getUserInfo,
  ssoAxiosInstance,
} from "@/utils";
import {
  isTransientError,
  retryOnTransientError,
} from "@/utils/axios/baseAxiosInstance";
import employeeExperienceV1AxiosInstance from "@/utils/axios/employeeExperienceV1AxiosInstance";
import { getPlatformHeader } from "@/utils/deviceDetails";
import { isRhiV2QuoteExpired, isRhiV2QuoteLocked } from "@/utils/rhiV2Error";

// Extended interface for quote requests with riders
interface IExtendedQuotesRequest extends IGetQuotesRequest {
  riderIds?: Array<string>;
  planIds?: Array<string>;
}

const EKYC_REQUEST_TIMEOUT_MS = 125000;

export interface CreateOtpPayload {
  phoneNumber: string;
  aadharNumber: string;
  email: string;
  gender: string;
  nameAsPerAadhaar: string;
  dateOfBirth: string;
}

export interface VerifyOtpPayload {
  otp: string;
}

interface EkycMutationArgs<TPayload> {
  payload: TPayload;
  signal?: AbortSignal;
}

export class QuoteApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'QuoteApiError';
    this.code = code;
  }
}

export interface IProps {
  name: string;
  phone?: string;
  email?: string;
  productId?: string;
  query?: string;
  source?: string;
  isOffline?: boolean;
  info?: Record<string, unknown>;
  callbackRequested?: boolean;
  callbackMethod?: string;
  pincode?: string;
}

export const createLeadPayload = (props: IProps): IProps => {
  return {
    name: props.name,
    phone: props.phone,
    email: props.email,
    productId: AppConfig.RETAIL_HEALTH_INSURANCE_PRODUCT_ID,
    query: props.query || '',
    source: props.source || '',
    isOffline: props.isOffline || false,
    info: props.info,
    callbackMethod: props.callbackMethod,
    callbackRequested: props.callbackRequested,
    pincode: props.pincode
  };
}

export const createLeadQuery = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_LEAD],
    mutationFn: async (requestBody: IProps) => {
      const url = `crm/leads`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);

      return response.data.data;
    },
  };
};

export const getRHILeads = () => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_LEADS],
    queryFn: async () => {
      const url = `crm/products/${AppConfig.RETAIL_HEALTH_INSURANCE_PRODUCT_ID}/leads?page=1&pageSize=10`;
      const response = await employeeExperienceV1AxiosInstance.get(url);

      return response.data;
    },
  };
};

export const updateLead = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.UPDATE_LEAD],
    mutationFn: async (leadId: string) => {
      const url = `crm/leads/${leadId}`;
      const response = await employeeExperienceV1AxiosInstance.patch(url, { callbackRequested: true });
      return response.data;
    },
  };
};

export const getConfig = (
) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_CONFIG],
    queryFn: async () => {
      const url = `/rhi/config`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data;
    },
  };
};


export const createRhiV2Quote = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_RHI_V2_QUOTE],
    mutationFn: async (requestBody: IRhiV2QuoteRequest) => {
      const url = `/relay/rhi-v2/journey/quotes`;
      const response = await ssoAxiosInstance.post<IRhiV2CreateQuoteResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getRhiV2Quote = (quoteId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_QUOTE, quoteId, RHI_QUERY_KEYS.PERSIST],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/quotes`;
      const response = await ssoAxiosInstance.get<IRhiV2QuoteResponse>(url, {
        params: { quoteId },
      });
      return response.data;
    },
    enabled: !!quoteId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    refetchInterval: (query: {
      state: { data?: IRhiV2QuoteResponse; status?: string; error?: unknown };
    }) =>
      (query.state.status !== 'error' ||
        isTransientError(query.state.error)) &&
      query.state.data?.status === 'IN_PROGRESS'
        ? RHI_TIMEOUT_VALUES.RHI_V2_QUOTE_POLL_INTERVAL_MS
        : false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
};

export const getRhiV2Selection = (quoteId: string, planId: string) => {
  return {
    queryKey: [
      RHI_QUERY_KEYS.GET_RHI_V2_SELECTION,
      quoteId,
      planId,
      RHI_QUERY_KEYS.PERSIST,
    ],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/selection`;
      const response = await ssoAxiosInstance.get<IRhiV2Selection>(url, {
        params: { quoteId, planId },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
};

export const updateRhiV2Selection = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.UPDATE_RHI_V2_SELECTION],
    mutationFn: async (requestBody: IRhiV2SelectionRequest) => {
      const url = `/relay/rhi-v2/journey/selection`;
      const response = await ssoAxiosInstance.post<IRhiV2Selection>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const generateQuote = (leadId: string, sumInsured: number, includeEssentialRiders: boolean, options?: {
  retry?: number;
  retryDelay?: number;
  throwOnError?: boolean;
}, riders?: Array<string>, planIds?: Array<string>, signal?: AbortSignal) => {
  return {
    queryKey: [RHI_QUERY_KEYS.RETAIL_HEALTH_INSURANCE_QUOTES, leadId, sumInsured, includeEssentialRiders, riders, planIds],
    queryFn: async () => {
      const requestBody: IExtendedQuotesRequest = {
        leadId,
        sumInsured,
        includeEssentialRiders
      };

      // Add riders and planIds if provided
      if (riders) {
        requestBody.riderIds = riders;
      }
      if (planIds && planIds.length > 0) {
        requestBody.planIds = planIds;
      }

      const url = `/rhi/generateQuote`;
      const response = await employeeExperienceAxiosInstance.post(url, requestBody, {
        signal,
      });
      const plans = response.data.data.plans;
      const errors = response.data.errors;

      if (errors?.length > 0 && (!plans || plans.length === 0)) {
        const firstError = errors[0] || {};
        throw new QuoteApiError(
          firstError.code || 'UNKNOWN',
          firstError.message || 'Failed to generate quote',
        );
      }

      return plans;
    },
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: options?.retry ?? 2, // Default 2 retries
    retryDelay: options?.retryDelay ?? 1000, // Default 1 second delay
    throwOnError: options?.throwOnError ?? true, // Default throw on error
  };
};

export const generatePurchaseIntent = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.GENERATE_PURCHASE_INTENT],
    mutationFn: async (requestBody: IPurchaseIntentPayload) => {
      const url = `/rhi/quote/${quoteId}/show-interest`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody, {
        headers: { platform: getPlatformHeader() },
      });
      return response.data.data;
    },
  };
};


export const getMedicalQuestions = (quoteId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_MEDICAL_QUESTIONS, quoteId, RHI_QUERY_KEYS.PERSIST],
    queryFn: async () => {
      const url = `/rhi/quotes/${quoteId}/medical-questionaires`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data.data;
    },
  };
}

export const submitMedicalQuestions = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.SUBMIT_MEDICAL_QUESTIONS, quoteId],
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/medical-questionaires`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody, {
        headers: { platform: getPlatformHeader() },
      });
      return response.data;
    },
  };
}

export const createOtp = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_OTP, quoteId],
    mutationFn: async ({
      payload,
      signal,
    }: EkycMutationArgs<CreateOtpPayload>) => {
      const url = `/rhi/quote/${quoteId}/ekyc/initiate`;
      const response = await employeeExperienceV1AxiosInstance.post(url, payload, {
        timeout: EKYC_REQUEST_TIMEOUT_MS,
        signal,
        headers: { platform: getPlatformHeader() },
      });
      return response.data;
    },
  };
};

export const verifyOtp = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.VERIFY_OTP, quoteId],
    mutationFn: async ({
      payload,
      signal,
    }: EkycMutationArgs<VerifyOtpPayload>) => {
      const url = `/rhi/quote/${quoteId}/ekyc/submit`;
      const response = await employeeExperienceV1AxiosInstance.post(url, payload, {
        timeout: EKYC_REQUEST_TIMEOUT_MS,
        signal,
        headers: { platform: getPlatformHeader() },
      });
      return response.data.data;
    },
  };
};

export const addNominee = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.ADD_NOMINEE, quoteId],
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/nominee-details`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);
      return response.data.data;
    },
  };
};

export const addBankDetails = (quoteId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.ADD_BANK_DETAILS, quoteId],
    mutationFn: async (requestBody: unknown) => {
      const url = `/rhi/quotes/${quoteId}/bank-details`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);
      return response.data.data;
    },
  };
};

export const getProposal = (proposalId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_PROPOSAL, proposalId],
    queryFn: async () => {
      const url = `rhi/proposals/${proposalId}/checkout-link`;
      const response = await employeeExperienceV1AxiosInstance.get(url);
      return response.data;
    },
  };
};

export const getPolicyStatus = (requestedD2cUserId?: string, forceCheck?: boolean) => {
  const userInfo = getUserInfo();
  const tokenInfo = decodeAuthToken();
  const d2cUserId = requestedD2cUserId || userInfo?.d2cUserId || tokenInfo?.d2cUserId;
  return {
    queryKey: [RHI_QUERY_KEYS.GET_POLICY_STATUS, d2cUserId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (forceCheck) params.set('forceCheck', 'true');
      const query = params.size > 0 ? `?${params.toString()}` : '';
      const response = await employeeExperienceV1AxiosInstance.get(`/rhi/policy-status/${d2cUserId}${query}`);
      return response.data;
    },
  };
};

export const paymentCallback = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.PAYMENT_CALLBACK],
    mutationFn: async (transactionId: string) => {
      const url = `/rhi/payment-callback/`;
      const response = await employeeExperienceV1AxiosInstance.post(
        url,
        {
          transactionId,
        },
        {
          headers: { platform: getPlatformHeader() },
        },
      );
      return response.data;
    },
  };
};

export type SuperleapLeadStatus = 'created' | 'skipped' | 'failed';

export interface ISuperleapCreateLeadResponse {
  status: SuperleapLeadStatus;
  superleapLeadId: string | null;
  reason: string | null;
}

export interface ISuperleapMemberDetailPayload {
  relationship: string;
  gender: string;
  dob: string;
}

export interface ISuperleapCreateOpportunityRequest {
  memberDetails: ISuperleapMemberDetailPayload[];
}

export type SuperleapOpportunityStatus = 'created' | 'failed';

export interface ISuperleapCreateOpportunityResponse {
  status: SuperleapOpportunityStatus;
  superleapLeadId: string | null;
  opportunityId: string | null;
  reason: string | null;
}

export const createSuperleapLead = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_SUPERLEAP_LEAD],
    mutationFn: async (): Promise<ISuperleapCreateLeadResponse> => {
      const url = `/relay/rhi-v2/journey/superleap/lead`;
      const response = await ssoAxiosInstance.post(url, {});
      return response.data;
    },
  };
};

export const createSuperleapOpportunity = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_SUPERLEAP_OPPORTUNITY],
    mutationFn: async (
      requestBody: ISuperleapCreateOpportunityRequest,
    ): Promise<ISuperleapCreateOpportunityResponse> => {
      const url = `/relay/rhi-v2/journey/superleap/opportunity`;
      const response = await ssoAxiosInstance.post(url, requestBody);
      return response.data;
    },
  };
};

export interface ISuperleapCreateLeadResponseV1 {
  status: SuperleapLeadStatus;
  superleapLeadId?: string;
  reason?: string;
}

export interface ISuperleapCreateOpportunityRequestV1 {
  leadId: string;
  memberDetails: ISuperleapMemberDetailPayload[];
}

export type SuperleapOpportunityStatusV1 = 'created' | 'exists' | 'failed';

export interface ISuperleapCreateOpportunityResponseV1 {
  status: SuperleapOpportunityStatusV1;
  superleapLeadId?: string;
  opportunityId?: string;
}

export const createSuperleapLeadV1 = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_SUPERLEAP_LEAD],
    mutationFn: async (): Promise<ISuperleapCreateLeadResponseV1> => {
      const url = `/rhi/superleap/lead`;
      const response = await employeeExperienceV1AxiosInstance.post(url, {});
      return response.data;
    },
  };
};

export const createSuperleapOpportunityV1 = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.CREATE_SUPERLEAP_OPPORTUNITY],
    mutationFn: async (
      requestBody: ISuperleapCreateOpportunityRequestV1,
    ): Promise<ISuperleapCreateOpportunityResponseV1> => {
      const url = `/rhi/superleap/opportunity`;
      const response = await employeeExperienceV1AxiosInstance.post(url, requestBody);
      return response.data;
    },
  };
};

export const getRhiV2JourneyList = (limit?: number) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_JOURNEY_LIST, limit ?? 'default'],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/list`;
      const response = await ssoAxiosInstance.get<IRhiV2JourneyList>(url, {
        params: limit ? { limit } : undefined,
      });
      return response.data;
    },
  };
};

export const commitRhiV2Selection = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.COMMIT_RHI_V2_SELECTION],
    mutationFn: async (requestBody: IRhiV2CommitRequest) => {
      const url = `/relay/rhi-v2/journey/selection/commit`;
      const response = await ssoAxiosInstance.post<IRhiV2Selection>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getRhiV2ProposalQuestions = (quoteId: string, planId: string) => {
  return {
    queryKey: [
      RHI_QUERY_KEYS.GET_RHI_V2_PROPOSAL_QUESTIONS,
      quoteId,
      planId,
    ],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/proposal/questions`;
      const response = await ssoAxiosInstance.get<IRhiV2ProposalForm>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: (failureCount: number, error: unknown) =>
      !isRhiV2QuoteLocked(error) && !isRhiV2QuoteExpired(error) && failureCount < 3,
    gcTime: 0,
    staleTime: 0,
  };
};

export const saveRhiV2ProposalAnswers = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.SAVE_RHI_V2_PROPOSAL_ANSWERS],
    mutationFn: async ({
      proposalId,
      ...requestBody
    }: IRhiV2SaveAnswersRequest) => {
      const url = `/relay/rhi-v2/journey/proposal/${proposalId}/answers`;
      const response = await ssoAxiosInstance.patch<IRhiV2SaveAnswersResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

// Two insurer calls sit behind an upload; the spec allows up to ~90 s, so outlast the relay.
const RHI_V2_DOCUMENT_UPLOAD_TIMEOUT_MS = 120_000;

export const uploadRhiV2ProposalDocument = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.UPLOAD_RHI_V2_PROPOSAL_DOCUMENT],
    mutationFn: async ({
      proposalId,
      ...requestBody
    }: IRhiV2UploadDocumentRequest) => {
      const url = `/relay/rhi-v2/journey/proposal/${proposalId}/documents`;
      const response = await ssoAxiosInstance.post<IRhiV2UploadDocumentResponse>(
        url,
        requestBody,
        { timeout: RHI_V2_DOCUMENT_UPLOAD_TIMEOUT_MS },
      );
      return response.data;
    },
  };
};

export const affirmRhiV2Proposal = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.AFFIRM_RHI_V2_PROPOSAL],
    mutationFn: async ({ proposalId, affirmedAnswersHash }: IRhiV2AffirmRequest) => {
      const url = `/relay/rhi-v2/journey/proposal/${proposalId}/affirm`;
      const response = await ssoAxiosInstance.post<IRhiV2AffirmResponse>(url, {
        affirmedAnswersHash,
      });
      return response.data;
    },
  };
};

// Every Proceed re-sends the proposal to the insurer; the guide allows up to ~90 s (2026-10-20).
const RHI_V2_SUBMIT_TIMEOUT_MS = 120_000;

export const submitRhiV2Proposal = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.SUBMIT_RHI_V2_PROPOSAL],
    mutationFn: async ({ proposalId, idempotencyKey }: IRhiV2SubmitRequest) => {
      const url = `/relay/rhi-v2/journey/proposal/${proposalId}/submit`;
      const response = await ssoAxiosInstance.post<IRhiV2SubmitResponse>(
        url,
        undefined,
        {
          headers: { 'Idempotency-Key': idempotencyKey },
          timeout: RHI_V2_SUBMIT_TIMEOUT_MS,
        },
      );
      return response.data;
    },
  };
};

export const getRhiV2PaymentSummary = (quoteId: string, planId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_PAYMENT_SUMMARY, quoteId, planId],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/payment/summary`;
      const response = await ssoAxiosInstance.get<IRhiV2PaymentSummary>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    gcTime: 0,
    staleTime: 0,
  };
};

export const initiateRhiV2Payment = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.INITIATE_RHI_V2_PAYMENT],
    mutationFn: async ({
      idempotencyKey,
      ...requestBody
    }: IRhiV2PaymentInitiateRequest) => {
      const url = `/relay/rhi-v2/journey/payment/initiate`;
      const response = await ssoAxiosInstance.post<IRhiV2PaymentInitiateResponse>(
        url,
        requestBody,
        { headers: { 'Idempotency-Key': idempotencyKey } },
      );
      return response.data;
    },
  };
};

export const getRhiV2PaymentStatus = (quoteId: string, planId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_PAYMENT_STATUS, quoteId, planId],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/payment/status`;
      const response = await ssoAxiosInstance.get<IRhiV2PaymentStatus>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

export const getRhiV2PolicyDocument = (quoteId: string, planId: string) => {
  return {
    mutationKey: [RHI_QUERY_KEYS.GET_RHI_V2_POLICY_DOCUMENT, quoteId, planId],
    mutationFn: async () => {
      const url = `/relay/rhi-v2/journey/policy/document`;
      const response = await ssoAxiosInstance.get<IRhiV2PolicyDocument>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
  };
};

export const getRhiV2KycForm = (quoteId: string, planId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_KYC_FORM, quoteId, planId],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/kyc/form`;
      const response = await ssoAxiosInstance.get<IRhiV2KycForm>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

const isCacheable = (cacheControl: unknown) =>
  /max-age=\d+/i.test(String(cacheControl ?? '')) &&
  !/no-store/i.test(String(cacheControl ?? ''));

export const getRhiV2Pincode = (pincode: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_PINCODE, pincode],
    queryFn: async (): Promise<IRhiV2PincodeLookup> => {
      const url = `/relay/rhi-v2/journey/pincode`;
      const response = await ssoAxiosInstance.get<IRhiV2PincodeResponse>(url, {
        params: { pincode },
      });
      return {
        ...response.data,
        cacheable: isCacheable(response.headers['cache-control']),
      };
    },
    // A day when the server allows it; a `no-store` answer is asked again next time.
    staleTime: ({ state }: { state: { data?: IRhiV2PincodeLookup } }) =>
      state.data?.cacheable ? RHI_TTL_VALUES.RHI_V2_PINCODE : 0,
    gcTime: RHI_TTL_VALUES.RHI_V2_PINCODE,
    retry: false,
  };
};

export const getRhiV2Ifsc = (ifsc: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_IFSC, ifsc],
    queryFn: async (): Promise<IRhiV2IfscLookup> => {
      const url = `/relay/rhi-v2/journey/ifsc`;
      const response = await ssoAxiosInstance.get<IRhiV2IfscResponse>(url, {
        params: { ifsc },
      });
      return {
        ...response.data,
        cacheable: isCacheable(response.headers['cache-control']),
      };
    },
    // Same as the pincode: a day when the server allows it, asked again after a `no-store`.
    staleTime: ({ state }: { state: { data?: IRhiV2IfscLookup } }) =>
      state.data?.cacheable ? RHI_TTL_VALUES.RHI_V2_IFSC : 0,
    gcTime: RHI_TTL_VALUES.RHI_V2_IFSC,
    retry: false,
  };
};

export const initiateRhiV2Kyc = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.INITIATE_RHI_V2_KYC],
    mutationFn: async (requestBody: IRhiV2KycInitiateRequest) => {
      const url = `/relay/rhi-v2/journey/kyc/initiate`;
      const response = await ssoAxiosInstance.post<IRhiV2KycInitiateResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const verifyRhiV2KycOtp = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.VERIFY_RHI_V2_KYC_OTP],
    mutationFn: async (requestBody: IRhiV2KycVerifyOtpRequest) => {
      const url = `/relay/rhi-v2/journey/kyc/verify-otp`;
      const response = await ssoAxiosInstance.post<IRhiV2KycInitiateResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const getRhiV2KycState = (quoteId: string, planId: string) => {
  return {
    queryKey: [RHI_QUERY_KEYS.GET_RHI_V2_KYC_STATE, quoteId, planId],
    queryFn: async () => {
      const url = `/relay/rhi-v2/journey/kyc/state`;
      const response = await ssoAxiosInstance.get<IRhiV2KycStateResponse>(url, {
        params: { quoteId, planId: Number(planId) },
      });
      return response.data;
    },
    enabled: !!quoteId && !!planId,
    retry: retryOnTransientError,
    retryDelay: 1000,
    gcTime: 0,
    staleTime: 0,
  };
};

export const acceptRhiV2KycPrice = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.ACCEPT_RHI_V2_KYC_PRICE],
    mutationFn: async (requestBody: IRhiV2KycAcceptPriceRequest) => {
      const url = `/relay/rhi-v2/journey/kyc/accept-price`;
      const response = await ssoAxiosInstance.post<IRhiV2KycAcceptPriceResponse>(
        url,
        requestBody,
      );
      return response.data;
    },
  };
};

export const sendRhiV2ProposalForReview = () => {
  return {
    mutationKey: [RHI_QUERY_KEYS.SEND_RHI_V2_PROPOSAL_FOR_REVIEW],
    mutationFn: async (proposalId: string) => {
      const url = `/relay/rhi-v2/journey/proposal/${proposalId}/send-for-review`;
      const response =
        await ssoAxiosInstance.post<IRhiV2SendForReviewResponse>(url);
      return response.data;
    },
  };
};
